import {
  AppointmentStatusIcon,
  CheckboxActive,
  CheckboxDefault,
  Location,
} from '@/assets/svg';
import CustomSelect from '@/components/CustomSelect';
import MutipleSelection from '@/components/CustomSelect/MutipleSelection';
import { getDateRange, handleSelectAll, renderValue } from '@/helpers';
import {
  AllUsersAppointmentStatus,
  AllUsersAppointmentType,
  PAYMENT_SUPPRESSED,
  TeleAppointmentPaymentStatus,
  TeleAppointmentStatus,
  TeleAppointmentType,
} from '@/module/appointment-management/constants';
import { ButtonEnhance } from '@/module/common/componentUI/commonStyleComponents';
import CustomAsyncAutoComplete from '@/new-components/CustomAsyncAutoComplete';
import CustomChip from '@/new-components/CustomChip';
import CustomDate, { QUICK_RANGE } from '@/new-components/CustomDate';
import CustomSelectContainer from '@/new-components/CustomSelectOld/CustomSelectContainer';
import { sortAlphabetically } from '@/utils/sort';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Hidden,
  makeStyles,
  Paper,
  TextField,
  withStyles,
} from '@material-ui/core';
import { ArtTrackOutlined, Autorenew } from '@material-ui/icons';
import { isBoolean, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const CustomButton = withStyles({
  root: {
    margin: 5,
    paddingLeft: 40,
    paddingRight: 40,

    height: 40,
    minWidth: 160,

    textTransform: 'unset',
    fontWeight: 600,
    borderRadius: 50,
  },
})(Button);

const CustomFilterInput = withStyles({
  root: {
    '& .MuiOutlinedInput-root': {
      height: 40,
      borderRadius: 8,

      '& input': {
        padding: 0,
        marginLeft: 0,
      },
      '& input::placeholder': {
        opacity: 1,
        color: '#999999',
      },
    },
  },
})(TextField);

const useStyles = makeStyles((theme) => ({
  isBtnSelected: {
    border: 'none',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
  checkboxIcon: {
    padding: 0,
    marginRight: 4,
  },
  tagStyle: {
    maxWidth: 'calc(100% - 60px)', // Adjust based on your needs
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  selectWrapper: {
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'transparent',
    },

    '& .MuiAutocomplete-option': {
      padding: 8,
      borderRadius: 8,
      margin: '2px 8px',
      '&:first-child': {
        marginTop: 8,
      },
      '&:last-child': {
        marginBottom: 8,
      },
      '&.MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: '#ffffff',
      },
    },
  },
}));

const GroupSearch = ({
  userId,
  appointmentTypes,
  setAppointmentTypes,
  filterOption,
  onFilterOptionClear,
  onFilterOptionChange,
  setFilterOption,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onResetFilterOps,
  listLocations = [],
  locationOpen,
  setLocationOpen,
  locationFilter,
  setLocationFilter,
  appointmentStatuses,
  setAppointmentStatuses,
  defaultStartDate = '',
  defaultEndDate = '',
  setPaxScreenPaymentStatus,
  paxScreenPaymentStatus,
  onCreateAppointment,
  paymentSupressed,
  setPaymentSupressed,
  createdStartDate,
  setCreatedStartDate,
  createdEndDate,
  setCreatedEndDate,
}) => {
  const classes = useStyles();
  const AppointmentType = userId
    ? AllUsersAppointmentType
    : TeleAppointmentType;
  const [quickRange, setQuickRange] = useState(null);
  const [open, setOpen] = useState(false);

  const AppointmentStatus = userId
    ? AllUsersAppointmentStatus
    : TeleAppointmentStatus;
  const onChangeStartDate = (value) => {
    const newStartDate = moment(value).startOf('date');
    setStartDate(newStartDate || defaultStartDate);
    setFilterOption({
      ...filterOption,
      startDate: newStartDate,
    });
    setQuickRange(null);
    if (!newStartDate) {
      onChangeEndDate(null);
    }
  };

  const onChangeEndDate = (value) => {
    const newEndDate = moment(value).endOf('date');
    setEndDate(newEndDate || defaultEndDate);
    setFilterOption({
      ...filterOption,
      endDate: newEndDate,
    });
    setQuickRange(null);
  };

  const onChangeCreatedDate = (key) => (value) => {
    if (key === 'createdStartDate') {
      setCreatedStartDate(value || '');
      setFilterOption({
        ...filterOption,
        createdStartDate: moment(value).startOf('date'),
      });
    } else {
      setCreatedEndDate(value || '');
      setFilterOption({
        ...filterOption,
        createdEndDate: moment(value).endOf('date'),
      });
    }
  };

  const onClickQuickRange = (rangeType) => () => {
    setQuickRange(rangeType);
    let [firstDate, lastDate] = getDateRange(rangeType);
    setStartDate(moment(firstDate));
    setEndDate(moment(lastDate));
    setFilterOption({
      ...filterOption,
      startDate: firstDate,
      endDate: lastDate,
    });
  };

  const isEmptyFilterParams = useMemo(() => {
    const filterKeys = Object.keys(filterOption);

    return filterKeys.every((key) =>
      isBoolean(filterOption[key])
        ? !filterOption[key]
        : isEmpty(filterOption[key])
    );
  }, [filterOption]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        marginBottom: 16,
      }}
    >
      <Grid container spacing={3}>
        <Grid item md={3} sm={6} xs={12}>
          <CustomAsyncAutoComplete
            multiple
            disableCloseOnSelect
            isLocation
            endpoint="locations/filter"
            method="post"
            nameKey="name"
            value={locationFilter}
            onChange={(e, value) => {
              setFilterOption({
                ...filterOption,
                locationIds: value.map((it) => it.id),
              });
              setLocationFilter(value);
            }}
            defaultFilter={{
              sortByName: true,
              hasPlatoName: true,
            }}
            getOptionLabel={(option) => option?.name}
            renderInput={(params) => (
              <CustomFilterInput
                {...params}
                variant="outlined"
                placeholder={isEmpty(locationFilter) ? 'All locations' : ''}
              />
            )}
            renderOption={(option, { selected }) => {
              return (
                <Box display="flex" gridGap={4}>
                  <Checkbox
                    icon={<CheckboxDefault width={20} height={20} />}
                    checkedIcon={<CheckboxActive width={20} height={20} />}
                    checked={selected}
                    color="primry"
                    className={classes.checkboxIcon}
                  />
                  <span>{option.name}</span>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) => {
              let newValue = value.filter((it) => locationFilter?.includes(it));
              return newValue.length === 1 ? (
                <div className={classes.tagStyle}>{newValue[0].name}</div>
              ) : newValue.length > 1 ? (
                <Box>Locations ({newValue.length})</Box>
              ) : (
                ''
              );
            }}
            PaperComponent={({ children, ...other }) => (
              <Paper
                {...other}
                style={{ width: 320 }}
                className={classes.selectWrapper}
              >
                {children}
              </Paper>
            )}
            style={{ width: '100%' }}
            isSelected={locationFilter.length === 1}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <CustomSelectContainer
            isFullWidth
            height="40px"
            filterValue={appointmentStatuses || []}
            data={AppointmentStatus.map((item) => ({
              key: item.value,
              value: item.key,
            }))}
            renderValue={renderValue(
              appointmentStatuses.map((item) => AppointmentStatus[item]),
              'Statuses',
              'All statuses'
            )}
            onClear={() => {
              setAppointmentStatuses([]);
              setFilterOption({
                ...filterOption,
                appointmentStatuses: [],
              });
            }}
            onChange={(e) => {
              const value = handleSelectAll(
                e.target.value,
                AppointmentStatus.map((item) => item.value),
                appointmentStatuses
              );
              setFilterOption({
                ...filterOption,
                appointmentStatuses: value,
              });
              setAppointmentStatuses(value);
            }}
            labelSelectAll={'All statuses'}
            useSelectAll
            checkedAll={
              AppointmentStatus.map((item) => item.value).length ===
              appointmentStatuses.length
            }
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <CustomSelectContainer
            isFullWidth
            height="40px"
            filterValue={paxScreenPaymentStatus}
            data={TeleAppointmentPaymentStatus}
            renderValue={renderValue(
              paxScreenPaymentStatus.map(
                (item) => TeleAppointmentPaymentStatus[item]
              ),
              'Payment Status',
              'All payment statuses'
            )}
            onClear={() => {
              setPaxScreenPaymentStatus([]);
              setFilterOption({
                ...filterOption,
                paxScreenPaymentStatus: [],
              });
            }}
            onChange={(e) => {
              const value = handleSelectAll(
                e.target.value,
                TeleAppointmentPaymentStatus.map((item) => item.value),
                paxScreenPaymentStatus
              );
              setFilterOption({
                ...filterOption,
                paxScreenPaymentStatus: value,
              });
              setPaxScreenPaymentStatus(value);
            }}
            labelSelectAll={'All payment statuses'}
            useSelectAll
            checkedAll={
              TeleAppointmentPaymentStatus.map((item) => item.value).length ===
              paxScreenPaymentStatus.length
            }
          />
        </Grid>
        <Hidden smDown>
          <Grid item md={3} sm={6} xs={12}></Grid>
        </Hidden>

        <Grid item md={3} sm={6} xs={12}>
          <CustomDate
            {...{
              selectedDate: startDate,
              onChange: onChangeStartDate,
              placeholder: 'Start Date',
              maxDate: endDate ? moment(endDate) : null,
              onClear: () => {
                setStartDate('');
                setEndDate('');
                setQuickRange(null);
                setFilterOption({
                  ...filterOption,
                  startDate: '',
                  endDate: '',
                });
              },
              customRange: (
                <div
                  style={{
                    display: 'flex',
                    padding: 16,
                    maxWidth: 352,
                    flexWrap: 'wrap',
                    borderBottom: '2px solid #ECEDF0',
                  }}
                >
                  {QUICK_RANGE.map((range) => (
                    <CustomChip
                      label={range}
                      selectedDate={quickRange}
                      onChange={onClickQuickRange(range)}
                    />
                  ))}
                </div>
              ),
            }}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <CustomDate
            {...{
              selectedDate: endDate,
              onChange: onChangeEndDate,
              placeholder: 'End Date',
              minDate: startDate ? moment(startDate) : null,
              onClear: () => {
                setEndDate('');
                setQuickRange(null);
                setFilterOption({
                  ...filterOption,
                  endDate: '',
                });
              },
            }}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <CustomSelectContainer
            isFullWidth
            height="40px"
            filterValue={paymentSupressed || []}
            data={PAYMENT_SUPPRESSED.map((item) => ({
              key: item.value,
              value: item.key,
            }))}
            renderValue={renderValue(
              paymentSupressed.map((item) => PAYMENT_SUPPRESSED[item]),
              'Payment Suppressed',
              'All payment suppressed'
            )}
            onClear={() => {
              setPaymentSupressed([]);
              setFilterOption({
                ...filterOption,
                isFreePayment: null,
              });
            }}
            onChange={(e) => {
              const value = handleSelectAll(
                e.target.value,
                PAYMENT_SUPPRESSED.map((item) => item.value),
                paymentSupressed
              );
              setFilterOption({
                ...filterOption,
                isFreePayment: isEmpty(value) ? null : true,
              });
              setPaymentSupressed(value);
            }}
          />
        </Grid>
        <Hidden smDown>
          <Grid item md={3} sm={6} xs={12}></Grid>
        </Hidden>
        <Grid item md={3} sm={6} xs={12}>
          <CustomDate
            {...{
              selectedDate: createdStartDate,
              onChange: onChangeCreatedDate('createdStartDate'),
              placeholder: 'Created Start Date',
              maxDate: createdEndDate ? moment(createdEndDate) : null,
              onClear: () => {
                setCreatedStartDate('');
                setQuickRange(null);
                setFilterOption({
                  ...filterOption,
                  createdStartDate: '',
                });
              },
            }}
          />
        </Grid>
        <Grid item md={3} sm={6} xs={12}>
          <CustomDate
            {...{
              selectedDate: createdEndDate,
              onChange: onChangeCreatedDate('createdEndDate'),
              placeholder: 'Created End Date',
              minDate: createdStartDate ? moment(createdStartDate) : null,
              onClear: () => {
                setCreatedEndDate('');
                setQuickRange(null);
                setFilterOption({
                  ...filterOption,
                  createdEndDate: '',
                });
              },
            }}
          />
        </Grid>

        <Grid container justify="flex-end" style={{ marginRight: 16 }}>
          <CustomButton
            onClick={() => {
              onResetFilterOps();
              setQuickRange(null);
            }}
            variant="contained"
            color="primary"
            endIcon={<Autorenew />}
            style={{ borderRadius: 50, margin: 5 }}
            disabled={isEmptyFilterParams}
          >
            Reset Filter
          </CustomButton>

          <CustomButton
            onClick={onCreateAppointment}
            variant="contained"
            color="primary"
          >
            Create New
          </CustomButton>
        </Grid>
      </Grid>
    </div>
  );
};

export const StyledButtonEnhance = styled(ButtonEnhance)`
  max-width: 160px;
  display: flex;
  align-self: flex-end;
  justify-content: center;
  margin: 12px;
  .add-group {
    display: flex;
    width: fit-content;
    align-items: center;
    margin-top: -2px;
    span {
      margin-right: 8px;
      color: #cacfd3;
    }
  }
`;

export default GroupSearch;
